import { Button, Dialog, DialogContent, DialogTitle, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useState, Suspense, lazy } from "react";

import { useFormatAgeRange, useNextTranslation } from "../hooks";
import { usePriceFormatter } from "../hooks/usePriceFormatter";
import { Price as PriceType } from "../types/Price";
import { NumberControl } from "./ProductAvailabilityPricingCategory/NumberControl";

const JSONTree = lazy(() => import("react-json-tree"));

export type ProductAvailabilityPricingCategoryProps = {
    label?: string | null | ReactNode;
    description: string | null;
    units: number;
    price: PriceType;
    min: number | null;
    max: number | null;
    minAge: number | null;
    maxAge: number | null;
    onUnitsChange: (units: number) => void;
    discountsAvailable: boolean;
    pricingData?: Record<string, unknown> | null;
    disabled?: boolean;
    maxParticipantsDepends: Array<{ explanation: string }> | null;
    showPrice?: boolean;
};

const Wrapper = styled("div")(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(0, 2),
    gridTemplateColumns: "auto min-content",
    alignItems: "start",
    gridTemplateAreas: '"label units" "price units"',
    '&[data-should-show-number-control="true"]': {
        gridTemplateColumns: "auto",
        gridTemplateAreas: '"label" "price"',
    },
}));

const Label = styled(Typography)({
    "&": {
        flexGrow: 100,
        gridArea: "label",
    },
});

const Price = styled(Typography)(({ theme }) => ({
    "&": {
        flexShrink: 0,
        gridArea: "price",
        fontSize: "0.75rem",
        color: theme.palette.text.secondary,
        transition: theme.transitions.create("opacity", { duration: theme.transitions.duration.short }),
        '[data-has-value="false"]': {
            opacity: 0.2,
        },
    },
}));

const Units = styled(NumberControl)({
    flexShrink: 0,
    gridArea: "units",
});

export function ProductAvailabilityPricingCategory({
    label,
    description,
    units,
    price,
    min,
    max,
    minAge,
    maxAge,
    onUnitsChange,
    disabled,
    pricingData,
    discountsAvailable,
    maxParticipantsDepends,
    showPrice = true,
}: ProductAvailabilityPricingCategoryProps) {
    const priceFormatter = usePriceFormatter(price.currency);
    const [isPricingDialogOpen, setIsPricingDialogOpen] = useState(false);
    const [t] = useNextTranslation("availabilityForm");
    const formatAgeRange = useFormatAgeRange();
    let ageString = formatAgeRange(minAge, maxAge);
    if (ageString) ageString = `(${ageString})`;

    let renderedPrice: ReactNode | null = (
        <Price data-has-value={price.amount > 0}>{priceFormatter(price.amount)}</Price>
    );
    if (pricingData) {
        // if we have pricingData, wrap the rendered price in a button that shows a modal for the pricing data
        renderedPrice = (
            <div>
                <Button variant="text" sx={{ p: 0 }} onClick={() => setIsPricingDialogOpen(true)} data-testid="price">
                    {renderedPrice}
                </Button>
                <PricingDataDialog
                    pricingData={pricingData}
                    open={isPricingDialogOpen}
                    handleClose={() => setIsPricingDialogOpen(false)}
                />
            </div>
        );
    }

    if (!showPrice) renderedPrice = null;

    const minMaxMessages: ReactNode = (() => {
        const hasMin = min !== null && min > 1;

        if (!units) return null;
        if (!hasMin && max !== units) return null; // only return messages if units = max OR there is a minimum greater than 1

        let text: string | undefined;
        if (hasMin && max && max <= 20) {
            text =
                min === max
                    ? t("message.pricingCategoryExactParticipants", { exact: min })
                    : t("message.pricingCategoryBetweenParticipants", { min, max });
        } else if (hasMin) {
            text = t("message.pricingCategoryMinParticipants", { min });
        } else if (max) {
            text = t("message.pricingCategoryMaxParticipants", { max });
        }

        if (text) {
            return (
                <Typography component="span" variant="caption">
                    {text}
                </Typography>
            );
        }
        return null;
    })();

    const maxDependsMessage = maxParticipantsDepends?.map(({ explanation }) => explanation).join(" / ");

    return (
        <Wrapper data-should-show-number-control={!!disabled}>
            <>
                {typeof label === "string" ? (
                    <Label title={label}>
                        {label} {ageString}
                    </Label>
                ) : (
                    label
                )}
                <div>
                    {description && <Typography variant="caption">{description}</Typography>}
                    {maxDependsMessage && (
                        <Typography variant="caption" component="p">
                            {maxDependsMessage}
                        </Typography>
                    )}
                    {renderedPrice}
                    {discountsAvailable && (
                        <>
                            <Typography component="span" variant="caption">
                                {t("message.discountsAvailable")}
                            </Typography>{" "}
                        </>
                    )}
                    {minMaxMessages}
                </div>
                {!disabled && <Units min={min} max={max} value={units} onChange={onUnitsChange} />}
            </>
        </Wrapper>
    );
}

type PricingDataDialogProps = {
    pricingData: Record<string, unknown>;
    open: boolean;
    handleClose: () => void;
};
const PricingDataDialog = ({ pricingData, open, handleClose }: PricingDataDialogProps) => {
    return (
        <Dialog onClose={handleClose} open={open} fullWidth maxWidth="lg">
            <DialogTitle>Pricing data</DialogTitle>
            <DialogContent>
                <Suspense fallback={<div>Loading...</div>}>
                    <JSONTree data={pricingData} theme="colors"></JSONTree>
                </Suspense>
            </DialogContent>
        </Dialog>
    );
};
